import appConstants from '../../../../appConstants';
import ReverseGeocoding from '../../../../lib/Map/Trimble/utils/ReverseGeocoding';
import { formatAddress } from '../../Dispatch/utils/utils';

const GEOCODE_TYPES = {
  SHIPMENT: 'SHIPMENT',
  PICKUP: 'PICKUP',
  DELIVERY_STOP: 'DELIVERY_STOP'
};

const getFormattedZip = (country, zip) => {
  if (!zip) return '';
  if (country === appConstants.COUNTRY_ABBREVIATION.US) return zip.split('-')[0];
  if (country === appConstants.COUNTRY_ABBREVIATION.CA)
    return zip.replaceAll(/\s+/g, '').toUpperCase();
  return zip;
};

const getSanitizedAddress = async (updatedLocation) => {
  const address = await ReverseGeocoding({
    latitude: updatedLocation.lat,
    longitude: updatedLocation.lng
  });
  // Do not send city/state/ZIP-only strings as sanitizedAddress when reverse geocode has no street line.
  if (!address?.StreetAddress?.trim()) {
    return null;
  }
  const formattedZip = getFormattedZip(address.CountryAbbreviation, address.Zip);
  return formatAddress({
    address: address.StreetAddress,
    city: address.City,
    state: address.State,
    zip: formattedZip
  });
};

export const GEOCODE_OPTIONS = {
  [GEOCODE_TYPES.SHIPMENT]: {
    apiName: 'UpdateShipmentCustomerLocation',
    getRequestParams: async (customer, updatedLocation, userData) => ({
      scenarioId: userData.scenarioID,
      planDate: userData.planDate,
      terminalId: userData.terminal.id,
      ...(customer.id && { customerId: customer.id }),
      ...(customer.rmaxShipperCode && { rmaxShipperCode: customer.rmaxShipperCode }),
      latitude: updatedLocation.lat,
      longitude: updatedLocation.lng,
      sanitizedAddress: await getSanitizedAddress(updatedLocation)
    })
  },
  [GEOCODE_TYPES.PICKUP]: {
    apiName: 'UpdatePickupCustomerLocation',
    getRequestParams: async (customer, updatedLocation, userData) => ({
      terminalCode: userData.terminal.code,
      pickupId: customer.pickupId,
      rmaxShipperCodes: customer.id,
      latitude: updatedLocation.lat,
      longitude: updatedLocation.lng,
      eventDateTime: new Date(),
      groupId: customer.groupId,
      driverId: customer.driverId,
      sanitizedAddress: await getSanitizedAddress(updatedLocation)
    })
  },
  [GEOCODE_TYPES.DELIVERY_STOP]: {
    apiName: 'UpdateDeliveryCustomerLocation',
    getRequestParams: async (customer, updatedLocation, userData) => ({
      terminalCode: userData.terminal.code,
      customerCode: customer.id,
      latitude: updatedLocation.lat,
      longitude: updatedLocation.lng,
      eventDateTime: new Date(),
      sanitizedAddress: await getSanitizedAddress(updatedLocation)
    })
  }
};

export const getShipmentCustomerData = (shipment) => {
  const commonData = {
    code: shipment.customerCode, // Guid for misc customers and number for normal customers
    isMiscellaneousCustomer: shipment.isMiscellaneousCustomer,
    type: GEOCODE_TYPES.SHIPMENT,
    isDelivery: shipment.isDelivery,
    latitude: shipment.latitude,
    longitude: shipment.longitude
  };
  if (shipment.isDelivery) {
    return {
      ...commonData,
      id: shipment.customerId,
      name: shipment.displayCustomerName,
      address: shipment.consigneeAddress,
      address2: shipment.consigneeAddress2,
      city: shipment.city,
      state: shipment.state,
      zip: shipment.zip,
      number: shipment.consigneeCode,
      isGeoCoded: shipment.isGeoCoded
    };
  }
  return {
    ...commonData,
    rmaxShipperCode: shipment.rmaxShipperCode,
    name: shipment.shipperName,
    address: shipment.shipperAddress,
    address2: shipment.shipperAddress2,
    city: shipment.shipperCity,
    state: shipment.shipperState,
    zip: shipment.shipperZip,
    number: shipment.shipperCode,
    isGeoCoded: shipment.isShipperGeoCoded
  };
};

export const getCustomerManagementGeocodingData = (
  customer,
  customerType = appConstants.CUSTOMER_TYPE.DELIVERY
) => {
  const commonData = {
    number: customer.code, // code starts with R for display purpose
    code: customer.code, // Guid for misc customers and number for normal customers
    name: customer.name,
    address: customer.address,
    address2: customer.address2,
    city: customer.city,
    state: customer.state,
    zip: customer.zip,
    latitude: customer.latitude,
    longitude: customer.longitude,
    type: GEOCODE_TYPES.SHIPMENT //use Shipment configuration to call Shipment Management geocoding API in Customer Managment as well.
  };
  if (customerType === appConstants.CUSTOMER_TYPE.DELIVERY) {
    return {
      ...commonData,
      id: customer.deliveryDetails.customerId,
      originalCustomerCode: customer.deliveryDetails.originalCustomerCode,
      isMiscellaneousCustomer: customer.deliveryDetails.isMiscCustomer,
      isGeoCoded: customer.deliveryDetails.isGeoCoded
    };
  }
  return {
    ...commonData,
    rmaxShipperCode: customer.pickupDetails.rmaxShipperCode,
    isGeoCoded: customer.pickupDetails.isGeoCoded
  };
};

export const getStopCustomerData = (stop) => {
  return {
    id: stop.code,
    number: stop.accountNumber,
    name: stop.name,
    address: stop.address,
    address2: stop.address2,
    city: stop.city,
    state: stop.state,
    zip: stop.zip,
    latitude: stop.latitude,
    longitude: stop.longitude,
    pickupId: stop.pickupId,
    isGeoCoded: stop.isGeoCoded,
    type: stop.pickupId ? GEOCODE_TYPES.PICKUP : GEOCODE_TYPES.DELIVERY_STOP,
    driverId: stop.driverId,
    groupId: stop.groupId
  };
};

export const getPickupCustomerData = (pickups) => {
  const primaryPickup = pickups.find((pickup) => pickup.isPrimary) || pickups[0];
  return {
    id: pickups.map((pickup) => pickup.rmaxShipperCode),
    number: primaryPickup.shipperAccountNumber,
    name: primaryPickup.shipperName,
    address: primaryPickup.shipperAddress,
    address2: primaryPickup.shipperAddress2,
    city: primaryPickup.shipperCity,
    state: primaryPickup.shipperState,
    zip: primaryPickup.shipperZip,
    latitude: primaryPickup.shipperLatitude,
    longitude: primaryPickup.shipperLongitude,
    pickupIds: pickups.map((pickup) => pickup.pickupId),
    isGeoCoded: primaryPickup.isGeoCoded,
    type: GEOCODE_TYPES.PICKUP,
    groupId: primaryPickup.groupId,
    driverId: null
  };
};
